/* eslint-disable react/jsx-wrap-multilines */
import styled from '@emotion/styled';
import { forwardRef, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import H3 from '@base/H3/H3';
import Tags from 'aquilla/core/atoms/Tag';
import { Spacings, mediaQuery, Fonts } from '@styles/index';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import breadcrumbs, { goBack } from '@utils/breadcrumbs';
import useBatchDetails from '@api/hooks/batch/useBatchDetails';

import { LANGUAGE_MAP } from '@cont/Content/FreeLesson/utils';
import P2 from '@base/P2/P2';

import { getIsSubscribedToOffline } from '@selectors/subscription';
import { useRouter } from 'next/router';
import { getBatchTag } from '@comp/BatchCard/util';
import useFreeTrial from '@cont/NewPreSubscribed/FreeTrial/useFreeTrial';
import useFreeTrialStore from '@cont/NewPreSubscribed/FreeTrial/freeTrialStore';
import Dynamic from '@base/Dynamic/Dynamic';
import { getIsLoggedIn } from '@selectors/auth';
import BreadCrumbs from '@base/BreadCrumbs/BreadCrumbs';
import BatchSmallHeader from './SmallHeader';

import HeaderEducators from './HeaderEducators';
import BatchDescription from './BatchDescription';
import BatchThumbnailV2 from './BatchThumbnailV2';
import BatchCTASection from './BatchCTASection';
import { getIsPartnerEducatorBatch, getIsStorePath } from './utils';

const FreeTrial = Dynamic(() => import('./FreeTrial'));
const ActiveSubscriptionModal = Dynamic(() =>
  import('@cont/NewPreSubscribed/FreeTrial/ActiveSubscriptionModal')
);
const FreePlanModal = Dynamic(() =>
  import('@cont/NewPreSubscribed/FreeTrial/FreePlanModal')
);

const Wrapper = styled.div`
  grid-column: span 24;
  margin-bottom: ${Spacings.SPACING_16B};
  margin-bottom: ${({ $isEnrolled }) =>
    $isEnrolled ? Spacings.SPACING_2B : Spacings.SPACING_12B};
  background: var(--color-base-0);
  ${mediaQuery.uptoMobile} {
    grid-column: span 10;
    margin-bottom: ${Spacings.SPACING_10B};
  }
`;

const Header = styled.div`
  background-color: var(--color-base-1);
  border-radius: ${Spacings.SPACING_2B};
  box-shadow: ${boxShadowGenerator()};
  transition: all 300ms ease;
  padding: ${({ $paddingBottom }) => $paddingBottom} ${Spacings.SPACING_12B};
  padding-bottom: ${Spacings.SPACING_8B};
  ${mediaQuery.uptoMobile} {
    margin: 0 -${Spacings.SPACING_4B};
    padding: ${Spacings.SPACING_4B};
    background-color: var(--color-base-1);
    box-shadow: none;
  }
`;

const BreadCrumb = styled(BreadCrumbs)`
  transition: all 300ms ease;
  & > * {
    box-shadow: ${boxShadowGenerator({
      yOffset: Spacings.SPACING_1B,
      blur: Spacings.SPACING_4B
    })};
  }
`;

const TopBar = styled.div`
  margin-bottom: ${Spacings.SPACING_8B};
  transition: all 300ms ease;
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const CourseInfo = styled.div`
  flex: 1;
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_4B};
  }
`;

const LanguageTag = styled(Tags)`
  width: fit-content;
  transition: all 300ms ease;
  margin-right: ${Spacings.SPACING_2B};
  ${mediaQuery.aboveTablet} {
    margin-bottom: ${Spacings.SPACING_2B};
  }
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.EXTRA_SMALL};
  }
`;

const CourseTitle = styled(H3)`
  transition: all 300ms ease;
  color: var(--color-text-primary);
  margin-bottom: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin: ${Spacings.SPACING_6} 0 ${Spacings.SPACING_3B};
  }
`;

const HeaderContentLeft = styled.div`
  width: 100%;
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const Flex = styled.div`
  display: flex;
  justify-content: space-between;
  ${mediaQuery.uptoMobile} {
    flex-direction: column;
  }
`;

const SyllabusTag = styled(P2)`
  text-transform: uppercase;
  display: inline;
  font-weight: 700;
  color: var(--color-i-blue);
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.EXTRA_SMALL};
  }
`;

const BatchHeader = forwardRef(
  ({
    batchUID,
    scroll,
    educatorSectionRef,
    handlePurchase,
    isRecordedOrHybrid,
    freeTrialData,
    isDurationBased
  }) => {
    const { batchDetails: batch, isValidating: isLoading } =
      useBatchDetails(batchUID);
    const batchGoalUID = batch?.goal?.uid;
    const isOfflineSubscription = useSelector(
      getIsSubscribedToOffline(batchGoalUID)
    );
    const subscribedNow = useRef(false);
    const { route } = useRouter();
    const isLoggedIn = useSelector(getIsLoggedIn);
    const isStoreRoute = getIsStorePath(route);
    const isPartnerBatch = getIsPartnerEducatorBatch(batch);
    const { hasFreeTrialSubscription } = useFreeTrial(batch?.goal?.uid);
    const isActivateFreeTrialClicked = useFreeTrialStore(
      (storeState) => storeState.buttonClicked
    );
    const showFreeTrialNudge =
      isLoggedIn && isActivateFreeTrialClicked && hasFreeTrialSubscription;
    const showSubscriptionNudge =
      isLoggedIn && isActivateFreeTrialClicked && !hasFreeTrialSubscription;

    if (!batch) return null;
    const {
      name,
      goal = {},
      languages = [],
      syllabusTag,
      isEnrolled
    } = batch || {};

    const renderLanguages = () => {
      const language = languages?.[0];
      return (
        <LanguageTag variant="hollow" key={language?.label} size="small">
          {LANGUAGE_MAP[language?.code]?.languageText.toUpperCase()}
        </LanguageTag>
      );
    };

    return (
      <Wrapper $isEnrolled={isEnrolled}>
        <BatchSmallHeader
          scroll={scroll}
          batchUID={batchUID}
          batch={batch}
          handlePurchase={handlePurchase}
          isRecordedOrHybrid={isRecordedOrHybrid}
        />
        <Header $paddingBottom={Spacings.SPACING_8B}>
          <TopBar>
            <BreadCrumb
              onBackClick={goBack}
              tags={breadcrumbs.getBatchTags(
                goal,
                isOfflineSubscription,
                isStoreRoute
              )}
            />
          </TopBar>
          <Flex>
            <BatchThumbnailV2 batch={batch} />
            <CourseInfo>
              {renderLanguages()}
              <SyllabusTag>{`${getBatchTag(batch)}${
                syllabusTag ? ' Batch' : ''
              }`}</SyllabusTag>
              <Flex>
                <HeaderContentLeft>
                  <CourseTitle lineClamp={0} as="h1">
                    {name}
                  </CourseTitle>
                  <HeaderEducators
                    batchUID={batchUID}
                    educatorSectionRef={educatorSectionRef}
                    isPartnerBatch={isPartnerBatch}
                  />
                  <BatchDescription batchUID={batchUID} />
                </HeaderContentLeft>
              </Flex>
            </CourseInfo>
          </Flex>
          <BatchCTASection
            batchUID={batchUID}
            batch={batch}
            isLoading={isLoading}
            handlePurchase={handlePurchase}
            isDurationBased={isDurationBased}
          />
          <FreeTrial
            freeTrialData={freeTrialData}
            goalUID={goal?.uid}
            onSubscribe={() => {
              subscribedNow.current = true;
            }}
            lps="Batch Page"
          />
          <ActiveSubscriptionModal
            open={showSubscriptionNudge}
            goalUID={goal?.uid}
          />
          <FreePlanModal open={showFreeTrialNudge} goalUID={goal?.uid} />
        </Header>
      </Wrapper>
    );
  }
);

BatchHeader.displayName = 'BatchHeader';

export default BatchHeader;

BatchHeader.propTypes = {
  scroll: PropTypes.bool.isRequired,
  batchUID: PropTypes.string.isRequired
};
